import React, { useState, useEffect } from "react";
import { Modal } from "@material-ui/core";
import { API } from "aws-amplify";
import swal from "sweetalert";
import config from "../../config/aws-exports";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import ReplyIcon from "@material-ui/icons/Reply";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import styles from "./ViewConcernsReason.module.scss";
import CircularProgress from "@material-ui/core/CircularProgress";

const ViewConcernModal = ({
  open,
  handleClose,
  reason,
  studentName,
  adminReplies: existingReplies = [],
  oid,
  bpid,
  eid,
  tid,
}) => {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [activeReplyIndex, setActiveReplyIndex] = useState(null);
  const [txtReply, setTxtReply] = useState("");
  const [adminReplies, setAdminReplies] = useState([]);
  const [studentConcern, setStudentConcern] = useState(reason || "");
  const [editingReply, setEditingReply] = useState(null);
  const [editReplyText, setEditReplyText] = useState("");
  const [isEditingConcern, setIsEditingConcern] = useState(false);
  const [editConcernText, setEditConcernText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAdminReplies(existingReplies || []);
    setEditingReply(null);
    setEditReplyText("");
    setShowReplyBox(false);
    setActiveReplyIndex(null);
    setTxtReply("");
  }, [existingReplies, eid]);

  useEffect(() => {
    setStudentConcern(reason || "");
  }, [reason]);

  useEffect(() => {
    if (open) {
      setEditingReply(null);
      setEditReplyText("");
      setShowReplyBox(false);
      setActiveReplyIndex(null);
      setTxtReply("");
    }
  }, [open, eid]);

  if (!open) return null;

  const handleReplyClick = (index) => {
    setActiveReplyIndex(index);
    setShowReplyBox(true);
    setTxtReply("");
  };

  const handleEditConcern = () => {
    setEditConcernText(studentConcern);
    setIsEditingConcern(true);
  };

  const handleSaveConcern = async () => {
    if (!editConcernText.trim()) {
      swal("Warning", "Concern can't be empty", "warning");
      return;
    }
    setLoading(true);
    try {
      await API.post(config.aws_cloud_logic_custom_name, "/UpdateIaConcern", {
        body: {
          oid,
          bpid,
          topicids: tid,
          eid,
          action: "EDIT_CONCERN",
          concern: editConcernText,
        },
      });

      setStudentConcern(editConcernText);
      setIsEditingConcern(false);
      setEditConcernText("");

      swal("Success", "Concern updated successfully", "success");
    } catch (error) {
      console.error("EDIT CONCERN ERROR", error);

      swal("Error", error?.message || "Failed to update concern", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConcern = () => {
    swal({
      title: "Delete Concern?",
      text: "Are you sure you want to delete this concern?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;
      setLoading(true);
      try {
        await API.post(config.aws_cloud_logic_custom_name, "/UpdateIaConcern", {
          body: {
            oid,
            bpid,
            topicids: tid,
            eid,
            action: "DELETE_CONCERN",
          },
        });

        setStudentConcern("");
        //setAdminReplies([]);

        swal("Success", "Concern deleted successfully", "success");
      } catch (err) {
        console.error(err);

        swal("Error", "Failed to delete concern", "error");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleStudentReply = async () => {
    if (!txtReply.trim()) {
      swal("Warning", "Reply can't be empty", "warning");
      return;
    }

    setLoading(true);

    try {
      const bodyParam = {
        oid,
        bpid,
        topicids: tid,
        eid,
        studentReply: txtReply,
        replyIndex: activeReplyIndex,
      };

      console.log("===== Reply Request =====");
      console.log("API Name:", config.aws_cloud_logic_custom_name);
      console.log("Payload:", bodyParam);

      const response = await API.post(
        config.aws_cloud_logic_custom_name,
        "/UpdateIaConcern", // notice the /
        {
          body: bodyParam,
        },
      );

      console.log("API Response", response);

      setAdminReplies((prev) =>
        prev.map((item, idx) => {
          if (idx === activeReplyIndex) {
            return {
              ...item,
              studentReplies: [
                ...(item.studentReplies || []),
                {
                  message: txtReply,
                  createdTime: new Date().toISOString(),
                },
              ],
            };
          }

          return item;
        }),
      );

      setTxtReply("");
      setActiveReplyIndex(null);
      setShowReplyBox(false);

      swal("Success", "Reply posted successfully", "success");
    } catch (error) {
      console.error("FULL ERROR:", error);

      console.log("Message:", error?.message);
      console.log("Response:", error?.response);
      console.log("Config:", error?.config);

      swal("Error", error?.message || "Failed to save reply", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditStudentReply = (
    adminReplyIndex,
    studentReplyIndex,
    currentMessage,
  ) => {
    setEditingReply({
      adminReplyIndex,
      studentReplyIndex,
    });

    setEditReplyText(currentMessage);
  };

  const handleSaveStudentReply = async () => {
    if (!editReplyText.trim()) return;
    setLoading(true);

    try {
      const { adminReplyIndex, studentReplyIndex } = editingReply;

      await API.post(config.aws_cloud_logic_custom_name, "/UpdateIaConcern", {
        body: {
          oid,
          bpid,
          topicids: tid,
          eid,
          action: "EDIT_STUDENT_REPLY",
          replyIndex: adminReplyIndex,
          studentReplyIndex,
          studentReply: editReplyText,
        },
      });

      setAdminReplies((prev) =>
        prev.map((reply, i) => {
          if (i !== adminReplyIndex) return reply;

          return {
            ...reply,
            studentReplies: reply.studentReplies.map((sr, j) =>
              j === studentReplyIndex
                ? {
                    ...sr,
                    message: editReplyText,
                  }
                : sr,
            ),
          };
        }),
      );

      setEditingReply(null);
      setEditReplyText("");

      swal("Success", "Reply updated successfully", "success");
    } catch (err) {
      console.error(err);
      swal("Error", "Failed to update reply", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudentReply = (adminReplyIndex, studentReplyIndex) => {
    swal({
      title: "Delete Reply?",
      text: "Are you sure?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;
      setLoading(true);

      try {
        await API.post(config.aws_cloud_logic_custom_name, "/UpdateIaConcern", {
          body: {
            oid,
            bpid,
            topicids: tid,
            eid,
            action: "DELETE_STUDENT_REPLY",
            replyIndex: adminReplyIndex,
            studentReplyIndex,
          },
        });

        setAdminReplies((prev) =>
          prev.map((reply, i) => {
            if (i !== adminReplyIndex) return reply;

            return {
              ...reply,
              studentReplies: reply.studentReplies.filter(
                (_, j) => j !== studentReplyIndex,
              ),
            };
          }),
        );

        swal("Success", "Reply deleted successfully", "success");
      } catch (err) {
        console.error(err);

        swal("Error", "Failed to delete reply", "error");
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={styles.modalContainer}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "rgba(255,255,255,0.6)",
              zIndex: 9999,
            }}
          >
            <CircularProgress
              style={{
                position: "fixed",
                top: "48%",
                left: "48%",
                background: "transparent",
                zIndex: "2001",
              }}
            />
          </div>
        )}
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Concern Discussion</h2>

          <button onClick={handleClose} className={styles.closeButton}>
            ×
          </button>
        </div>

        {/* Student Concern */}
        <div className={styles.studentConcern}>
          <AccountCircleIcon className={styles.accountIcon} />

          <div style={{ flex: 1 }}>
            <div className={styles.studentName}>{studentName}</div>
            {isEditingConcern ? (
              <div style={{ marginTop: "10px" }}>
                <textarea
                  value={editConcernText}
                  onChange={(e) => setEditConcernText(e.target.value)}
                  maxLength={150}
                  rows={4}
                  className={styles.editConcernTextarea}
                />

                <div className={styles.actionButtons}>
                  <button
                    onClick={() => {
                      setIsEditingConcern(false);
                      setEditConcernText("");
                    }}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleSaveConcern}
                    className={styles.saveButton}
                  >
                    Update
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.concernText}>
                {studentConcern || "No concern available"}
              </div>
            )}
          </div>

          <div className={styles.actionPanel}>
            <EditIcon onClick={handleEditConcern} className={styles.editIcon} />

            <DeleteOutlineIcon
              onClick={handleDeleteConcern}
              className={styles.deleteIcon}
            />
          </div>
        </div>

        {/* Admin Replies */}
        {adminReplies?.map((reply, index) => (
          <div key={index} className={styles.replyItem}>
            <div className={styles.replyContent}>
              <AccountCircleIcon className={styles.accountIcon} />

              <div style={{ flex: 1 }}>
                <div className={styles.studentName}>Admin</div>

                <div className={styles.replyMessage}>
                  {reply?.message || reply}
                </div>
              </div>

              <div className={styles.actionPanel}>
                <ReplyIcon
                  onClick={() => {
                    if ((reply.studentReplies || []).length < 1) {
                      handleReplyClick(index);
                    }
                  }}
                  className={styles.replyIcon}
                  style={{
                    cursor:
                      (reply.studentReplies || []).length >= 1
                        ? "not-allowed"
                        : "pointer",

                    opacity: (reply.studentReplies || []).length >= 1 ? 0.5 : 1,

                    color:
                      (reply.studentReplies || []).length >= 1
                        ? "#ccc"
                        : "#ff6a00",
                  }}
                />
              </div>
            </div>

            {showReplyBox && activeReplyIndex === index && (
              <div className={styles.replySection}>
                <textarea
                  value={txtReply}
                  onChange={(e) => setTxtReply(e.target.value)}
                  maxLength={150}
                  rows={4}
                  placeholder="Reply..."
                  className={styles.replyInput}
                />

                <div className={styles.replyButtonsContainer}>
                  <button
                    onClick={() => {
                      setShowReplyBox(false);
                      setActiveReplyIndex(null);
                      setTxtReply("");
                    }}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleStudentReply}
                    className={styles.saveButton}
                  >
                    Send
                  </button>
                </div>
              </div>
            )}

            {/* Student Replies */}
            {reply?.studentReplies?.map((studentReply, idx) => (
              <div key={idx} className={styles.studentReplyContainer}>
                <div className={styles.contentWrapper}>
                  <AccountCircleIcon className={styles.accountIcon} />

                  <div style={{ flex: 1 }}>
                    <div className={styles.studentName}>{studentName}</div>

                    {editingReply?.adminReplyIndex === index &&
                    editingReply?.studentReplyIndex === idx ? (
                      <div style={{ marginTop: "5px" }}>
                        <textarea
                          value={editReplyText}
                          onChange={(e) => setEditReplyText(e.target.value)}
                          maxLength={150}
                          rows={3}
                          className={styles.editReplyTextarea}
                        />

                        <div className={styles.replyActionButtons}>
                          <button
                            onClick={() => {
                              setEditingReply(null);
                              setEditReplyText("");
                            }}
                            className={styles.cancelButton}
                          >
                            Cancel
                          </button>

                          <button
                            onClick={handleSaveStudentReply}
                            className={styles.saveButton}
                          >
                            Update
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.messageText}>
                        {studentReply.message}
                      </div>
                    )}
                  </div>

                  <div className={styles.actionPanel}>
                    <EditIcon
                      onClick={() =>
                        handleEditStudentReply(index, idx, studentReply.message)
                      }
                    />

                    <DeleteOutlineIcon
                      onClick={() => handleDeleteStudentReply(index, idx)}
                      className={styles.deleteIcon}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default ViewConcernModal;
